import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowRight, CalendarDays, Clock, List, Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { AuthField, AuthPhoneField } from 'components/auth';
import { LogoMark } from 'components/global/LogoMark';
import { formatDateLong } from 'lib/dateFormat';
import { UZ_E164_PATTERN } from 'lib/phone';
import * as AttemptModule from 'modules/attempt';
import config from 'config';
import { useTranslation } from 'react-i18next';

type GuestInfo = AttemptModule.Types.IApi.GuestInfo.Response;

/** "1-oktabr, 2026 · 14:00" read on the Tashkent clock, whatever the device's zone. */
export const formatTashkent = (iso?: string | null): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Tashkent',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        })
            .formatToParts(date)
            .map((part) => [part.type, part.value]),
    );
    const tashkentDay = new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
    return `${formatDateLong(tashkentDay)} · ${parts.hour}:${parts.minute}`;
};

/** Phones: white page under a 56px logo bar. Desktop: a 448px card on the exam ground, "Examing orqali" under it. */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen bg-card sm:bg-[#F4F5F7] sm:dark:bg-background">
            <a
                href={config.landing_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-14 items-center gap-2.5 border-b border-border px-4 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
                aria-label="Examing"
            >
                <LogoMark size={24} className="rounded-md" />
                <span className="font-display text-sm font-extrabold tracking-[-0.02em] text-foreground">Examing</span>
            </a>
            <div className="flex flex-col items-center sm:px-6 sm:pb-10 sm:pt-[120px]">
                <article className="w-full px-4 py-6 sm:max-w-[448px] sm:rounded-xl sm:border sm:border-border sm:bg-card sm:p-7 sm:shadow-[0_1px_3px_rgba(17,24,39,0.06)]">
                    {children}
                </article>
                <a
                    href={config.landing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="my-5 flex items-center gap-2 rounded-md text-[12.5px] text-muted-foreground transition-opacity hover:opacity-80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:mb-0"
                    aria-label="Examing"
                >
                    <LogoMark size={18} className="rounded-[5px]" />
                    {t('guest_powered_by')}
                </a>
            </div>
        </div>
    );
};

/** A grey panel inside the card for a test that isn't taking answers now. */
const StatePanel: React.FC<{ icon: React.ReactNode; title: string; children?: React.ReactNode }> = ({ icon, title, children }) => (
    <div className="mt-5 flex flex-col gap-1.5 rounded-[10px] border border-border bg-background p-4">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            {icon}
            {title}
        </span>
        {children}
    </div>
);

const GuestStartPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();

    const [info, setInfo] = useState<GuestInfo | null>(null);
    const [loadingInfo, setLoadingInfo] = useState(true);
    const [fullName, setFullName] = useState('');
    // E.164 ("+998901234567") or "" — the phone field shows the national part after a fixed +998.
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const loadInfo = useCallback(async () => {
        if (!token) return;
        setLoadingInfo(true);
        try {
            const { data } = await AttemptModule.Api.GuestInfo(token);
            setInfo(data);
        } catch {
            setInfo({ available: false, status: 'not_available' });
        } finally {
            setLoadingInfo(false);
        }
    }, [token]);

    useEffect(() => {
        loadInfo();
    }, [loadInfo]);

    const handleStart = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setError('');
        if (fullName.trim().length < 2) {
            setError(t('enter_your_full_name'));
            return;
        }
        // A real Uzbek number: +998 and 9 national digits.
        if (!UZ_E164_PATTERN.test(phone)) {
            setError(t('enter_a_valid_uzbek_phone_number'));
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await AttemptModule.Api.GuestStart({
                publicToken: token,
                fullName: fullName.trim(),
                phone,
            });
            sessionStorage.setItem(config.api.guest_token_key, data.sessionToken);
            navigate(`/s/${token}/session/${data.attempt.id}`, { replace: true });
        } catch (err: unknown) {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            setError(message || t('this_test_is_not_available'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingInfo) {
        return (
            <Shell>
                <div className="flex flex-col items-center gap-3 py-12" role="status">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
                    <p className="text-sm text-muted-foreground">{t('loading')}</p>
                </div>
            </Shell>
        );
    }

    const heading = (
        <>
            <h1 className="text-2xl font-bold leading-[1.25] tracking-[-0.02em] text-foreground">
                {info?.test?.title || t('assessment')}
            </h1>
            <p className="mt-2 text-[14.5px] leading-normal text-muted-foreground">
                {info?.test?.description || t('enter_your_details_to_begin')}
            </p>
        </>
    );

    // Opens later, closed, or not available: the same card with a grey panel.
    if (!info || !info.available) {
        const status = info?.status ?? 'not_available';
        return (
            <Shell>
                {heading}
                {status === 'not_open' ? (
                    <StatePanel icon={<CalendarDays className="h-4 w-4 text-foreground/80" aria-hidden />} title={t('this_test_opens_on')}>
                        <span className="text-xl font-bold tabular-nums text-foreground">{formatTashkent(info?.availableFrom)}</span>
                        <span className="text-[13px] text-muted-foreground">{t('guest_tashkent_time')}</span>
                    </StatePanel>
                ) : status === 'closed' ? (
                    <StatePanel icon={<Lock className="h-4 w-4 text-foreground/80" aria-hidden />} title={t('this_test_is_closed')}>
                        <span className="text-[13.5px] leading-normal text-muted-foreground">{t('guest_closed_desc')}</span>
                    </StatePanel>
                ) : (
                    <StatePanel icon={<AlertCircle className="h-4 w-4 text-foreground/80" aria-hidden />} title={t('this_test_is_not_available')} />
                )}
            </Shell>
        );
    }

    const questions = info.test?.totalQuestions;
    const minutes = info.test?.timeLimit;

    return (
        <Shell>
            {heading}

            {(typeof questions === 'number' || Boolean(minutes)) && (
                <div className="mt-4 flex gap-4 text-[13.5px] text-foreground/80">
                    {typeof questions === 'number' && (
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                            <List className="h-[15px] w-[15px] text-muted-foreground/70" aria-hidden />
                            {t('n_questions', { count: questions })}
                        </span>
                    )}
                    {minutes ? (
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                            <Clock className="h-[15px] w-[15px] text-muted-foreground/70" aria-hidden />
                            {minutes} {t('minutes')}
                        </span>
                    ) : null}
                </div>
            )}

            <form onSubmit={handleStart} className="mt-5 flex flex-col gap-3.5" noValidate>
                {error && (
                    <div
                        role="alert"
                        className="flex items-center gap-2.5 rounded-[10px] border border-error-200 bg-error-50 px-3 py-2.5 text-sm text-error-700 dark:border-error-900/50 dark:bg-error-900/20 dark:text-error-400"
                    >
                        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                        {error}
                    </div>
                )}

                <AuthField
                    name="fullName"
                    label={t('full_name')}
                    placeholder={t('full_name')}
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                />
                <AuthPhoneField label={t('phone_number')} name="phone" value={phone} onChange={setPhone} />

                <Button type="submit" className="mt-2 h-12 w-full gap-2 text-[15px] font-semibold" disabled={submitting}>
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            {t('starting')}
                        </>
                    ) : (
                        <>
                            {t('start_assessment')}
                            <ArrowRight className="h-4 w-4" aria-hidden />
                        </>
                    )}
                </Button>
            </form>
        </Shell>
    );
};

export default GuestStartPage;
